import type { ReactNode } from "react";
import { type FC, type PropsWithChildren, useCallback, useMemo, useEffect } from "react";
import { useTRPC } from "../trpc";
import { useMutation } from "@tanstack/react-query";

type Log = {
  level: "error" | "info" | "warn";
  message: string;
  meta: object;
};

const queue: Log[] = [];

export const LoggerProvider: FC<PropsWithChildren> = ({ children }): ReactNode => {
  const trpc = useTRPC();
  const { mutate } = useMutation(trpc.log.mutationOptions());

  window.onerror = useCallback((event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
    queue.push(
      {
        level: "error",
        message: "Browser error",
        meta: {
          event,
          source,
          colno,
          lineno,
          error,
        },
      },
    );
  }, []);

  window.logger = useMemo(() => {
    return {
      info: (meta: object, message: string) => {
        queue.push({
          level: "info",
          message,
          meta,
        });
      },
      error: (meta: object, message: string) => {
        queue.push({
          level: "error",
          message,
          meta,
        });
      },
      warn: (meta: object, message: string) => {
        queue.push({
          level: "warn",
          message,
          meta,
        });
      },
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (queue.length) {
        mutate(queue.splice(0, queue.length));
      }
    }, 2_000);

    return () => {
      clearInterval(interval);
    };
  }, [mutate]);

  return children;
};
